import { List, Map, Set, hash } from 'immutable';

import {
  Container,
  EditableSection,
  ListItem,
  ReadingListDetails,
  Section,
} from '®shared/reading-list/models';
import { TreeUtils } from '®shared/reading-list/structure-utils';

import {
  ContainerIdentifier,
  ContainerIdentifierWithIndex,
  EntryIdentifier,
} from './container-edit.actions';
import {
  ContainedTypeAndId,
  ContainedTypeAndIdTypes,
  ContainerStructure,
} from './container.state';

type ContainerOrItem = Container | ListItem;
type ItemOrSection = ListItem | Section;

export function getNodeChildren(
  containerOrItem: ContainerOrItem,
): ContainerOrItem[] {
  switch (containerOrItem.type) {
    case 'items':
      return [];
    case 'lists':
      return (containerOrItem as ReadingListDetails).all_children || [];
    case 'sections':
      return (containerOrItem as Section).all_children || [];
  }
}

export function extractSectionIdLookup(
  containerOrItem: ContainerOrItem,
  lookup = Map<string, EditableSection>(),
): Map<string, EditableSection> {
  // make a recursive reducer function for the specified depth
  const childReducer = (lookupSoFar, child) =>
    extractSectionIdLookup(child, lookupSoFar);

  switch (containerOrItem && containerOrItem.type) {
    // terminating case: items have no children, so cannot add more sections
    case 'items': {
      return lookup;
    }
    // recursive case: ignore list, but descend into list children
    case 'lists': {
      const list = containerOrItem as ReadingListDetails;
      return getNodeChildren(list).reduce(childReducer, lookup);
    }
    // recursive case: add section, then descend into section children
    case 'sections': {
      const section = containerOrItem as EditableSection;
      return getNodeChildren(section).reduce(
        childReducer,
        lookup.set(section.id, section),
      );
    }
  }
}

export function extractContainerStructure(
  containerOrItem: ContainerOrItem,
  lookup = Map<string, List<ContainedTypeAndId>>(),
): ContainerStructure {
  // make a recursive reducer function for the specified depth
  const childReducer = (lookupSoFar, child) =>
    extractContainerStructure(child, lookupSoFar);

  switch (containerOrItem && containerOrItem.type) {
    // terminating case: items have no children, so add this item and return
    case 'items': {
      return lookup;
    }
    // recursive case: descend into container children
    case 'lists':
    case 'sections': {
      const container = containerOrItem as Container;

      const containerChildren: List<ContainedTypeAndIdTypes> =
        container.all_children
          ? List(container.all_children.map(getContainedTypeAndIdOnly))
          : List();

      return getNodeChildren(container).reduce(
        childReducer,
        lookup.set(containerKey(container), containerChildren),
      );
    }
  }
}

function getChildrenFromStructure(
  parent: ContainedTypeAndId,
  containerStructure: ContainerStructure,
): List<ContainedTypeAndId> | null {
  if (parent.type === 'items') {
    return null;
  }
  const children = containerStructure.get(containerKey(parent));
  return children ? children : null;
}

function getStructureChildrenExtractor(containerStructure: ContainerStructure) {
  return (parent: ContainedTypeAndId) => {
    const children = getChildrenFromStructure(parent, containerStructure);
    return children ? children.toArray() : null;
  };
}

/**
 * Fetch all path readouts for a container structure (tree), traversing
 * in a depth-first fashion.
 *
 * For a tree like this:
 *
 *   {
 *     "lists::123": [{ type: "sections", id: "section1" }],
 *     "sections::section1": [{ type: "items", id: "item1" }, { type: "items", id: "item2" }]
 *   }
 *
 * the readout would be a list of depth-first readouts, such as this:
 *
 *   [
 *     ["lists::123", "sections::section1", "items::item1"],
 *     ["lists::123", "sections::section1", "items::item2"]
 *   ]
 *
 * @param  {ContainedTypeAndId} containerRoot      The root node for this container
 * @param  {ContainerStructure} containerStructure The container structure tree
 * @return {string[][]}                            All path readouts, each as an array of keys
 */
export function getPathReadouts(
  containerRoot: ContainedTypeAndId,
  containerStructure: ContainerStructure,
): string[][] {
  return TreeUtils.pathReadout<ContainedTypeAndId, string>(
    containerRoot,
    getStructureChildrenExtractor(containerStructure),
    containerKey,
  );
}

/**
 * Gets all path readouts for a container structure, indexed by the "leaf" (last component).
 *
 * For example, this readout:
 *
 *   ["lists::123", "sections::section1", "items::item1"]
 *    ^^ root-node                         ^^ leaf-node
 *
 * would convert to the following entry in a Map:
 *
 *   "items::item1": ["lists::123", "sections::section1", "items::item1"]
 *    ^^ leaf-node
 *
 * @param {Container} container - the container structure
 * @returns {Map<string, List<string>>} - all paths readouts for a container, indexed by last component (key)
 */
export function getPathReadoutsByLeaf(
  containerRoot: ContainedTypeAndId,
  containerStructure: ContainerStructure,
): Map<string, List<string>> {
  const pathReadouts: string[][] = getPathReadouts(
    containerRoot,
    containerStructure,
  );

  // group path readouts by the ID of the last component
  // (which will be unique, so the container will contain only one path)
  return List(pathReadouts)
    .map(pathReadout => (Array.isArray(pathReadout) ? List(pathReadout) : null))
    .filter(pathReadout => (pathReadout ? true : false))
    .groupBy(pathReadout => pathReadout.last(null)) // group by the last path component
    .map(groupedReadouts => groupedReadouts.last(List<string>())) // there's only one readout per path component
    .filter((value, key) => key !== null && !value.isEmpty())
    .toMap();
}

export function getAllKeysFromContainerStructure(
  containerRoot: ContainedTypeAndId,
  containerStructure: ContainerStructure,
): Set<ContainedTypeAndId> {
  return Set(
    TreeUtils.collectNodes<ContainedTypeAndId>(
      containerRoot,
      getStructureChildrenExtractor(containerStructure),
    ),
  );
}

export function containerKey({
  id,
  type,
}: {
  id: string;
  type: string;
}): string {
  return `${type}::${id}`;
}

export function keyToContainedTypeAndId(key: string): ContainedTypeAndId {
  const [, type, id] = key.match(/^(items|lists|sections)\:\:([^\:]+)$/);
  return { id, type } as ContainedTypeAndId;
}

export function getContainedTypeAndIdOnly<T extends ContainedTypeAndIdTypes>({
  id,
  type,
}: T | ListItem | ReadingListDetails | Section): T {
  const valueObject = {
    id,
    type,
    equals: (other: any): boolean => {
      return (
        other &&
        other.id &&
        other.type &&
        other.id === id &&
        other.type === type
      );
    },
    hashCode: () => hash(containerKey({ id, type })),
  };
  return valueObject as T;
}

export function getContainerIdentifier(
  container:
    | ContainedTypeAndId
    | Container
    | ContainerIdentifier
    | EditableSection,
): ContainerIdentifier {
  const { id, type } = container;
  return { id, type } as ContainerIdentifier;
}

export function getContainerWithIndex(
  container: ContainerIdentifier,
  index: number,
): ContainerIdentifierWithIndex {
  return { id: container.id, type: container.type, index: index };
}

export function getEntryIdentifier(entry: ItemOrSection): EntryIdentifier {
  const { id, type } = entry;
  return { id, type } as EntryIdentifier;
}
